import {
  UPDATE_URLS,
  ASSIGN_PACKAGE_TO_RESTAURANT,
  CREATE_RESTAURANT,
  EDIT_RESTAURANT,
  DELETE_RESTAURANT,
  GET_RESTAURANTS,
  UPLOAD_RESTAURANT_IMAGES,
  GET_RESTAURANT_DETAILS,
  GET_SUBMITTED_EMAILS
} from '../constants/UriConstants';
import apiClient from './apiClient';
import {
  CreateRestaurantRequestInterface,
  CreateRestaurantResponseInterface,
  EditRestaurantRequestInterface,
  RestaurantDetailsInterface,
  RestaurantImagesInterface,
  RestaurantsInterface,
  RestaurantUserEmailInterface
} from '../types/RestaurantInterface';

const appendBlobField = (formData: FormData, field: string, blob: Blob | null) => {
  if (blob) {
    formData.append(field, blob);
  }
};

const appendBlobFields = (formData: FormData, field: string, blobs: Blob[] | null | undefined) => {
  blobs?.forEach((blob) => formData.append(field, blob));
};

const appendJsonFields = (formData: FormData, fields: ReadonlyArray<readonly [string, unknown]>) => {
  fields.forEach(([field, value]) => {
    formData.append(field, JSON.stringify(value));
  });
};

/**
 * provides the package id to be assigned to a restaurant
 * @param managerPackageID - id of package that is currently owned by the user with restaurant (manager)
 * @param restaurantID (optional) - id of restaurant that package will be assigned to,
 * if none provided then will default to the currently selected restaurant
 * @return permissionToken that is generated based on the purchased package for a user
 */
export const assignPackageToRestaurant = (managerPackageID: number, restaurantID?: number): Promise<string> =>
  apiClient
    .post(ASSIGN_PACKAGE_TO_RESTAURANT, { managerPackageID }, { headers: { ...(restaurantID && { restaurantID }) } })
    .then((response) => response.data.permissionToken);

export const createRestaurant = (
  restaurant: CreateRestaurantRequestInterface
): Promise<CreateRestaurantResponseInterface> =>
  apiClient.post(CREATE_RESTAURANT, restaurant).then((response) => response.data);

export const editRestaurant = async (restaurant: EditRestaurantRequestInterface) => {
  await apiClient.put(EDIT_RESTAURANT, restaurant);
};

export const deleteRestaurant = async (restaurantID: number): Promise<void> => {
  await apiClient.delete(DELETE_RESTAURANT.replace('{restaurantID}', String(restaurantID)));
};

export const updateURL = async (reservationUrl?: string, orderingUrl?: string) => {
  await apiClient.put(UPDATE_URLS, {
    ...(reservationUrl !== undefined && { reservationUrl }),
    ...(orderingUrl !== undefined && { orderingUrl })
  });
};

export const getRestaurants = (): Promise<RestaurantsInterface> =>
  apiClient.get(GET_RESTAURANTS).then((response) => response.data);

export const getRestaurantDetails = (): Promise<RestaurantDetailsInterface> =>
  apiClient.get(GET_RESTAURANT_DETAILS).then((response) => response.data);

export const getSubmittedEmails = (): Promise<RestaurantUserEmailInterface[]> =>
  apiClient.get(GET_SUBMITTED_EMAILS).then((response) => response.data);

/**
 * uploads images specific to a restaurant domain
 *
 * Profile hero media (Home carousel): Prod accepts multiple `profile` file parts (images and video),
 * plus `profileOrder` and `profileImagesToDelete` — same ordering contract as gallery media.
 */
export const uploadRestaurantImages = (
  logoImage: Blob | null,
  thumbnailImage: Blob | null,
  profileImages: Blob[] | null,
  menuCoverImage: Blob | null,
  galleryImages: Blob[] | null,
  galleryImagesToDelete: number[],
  profileImagesToDelete: number[],
  imagesToDelete: number[],
  galleryOrder: string[],
  profileOrder: string[],
  restaurantID?: number
): Promise<RestaurantImagesInterface> => {
  const formData = new FormData();

  (
    [
      ['logo', logoImage],
      ['thumbnail', thumbnailImage],
      ['menuCover', menuCoverImage]
    ] as const
  ).forEach(([field, blob]) => appendBlobField(formData, field, blob));

  (
    [
      ['profile', profileImages],
      ['gallery', galleryImages]
    ] as const
  ).forEach(([field, blobs]) => appendBlobFields(formData, field, blobs));

  appendJsonFields(formData, [
    ['imagesToDelete', imagesToDelete],
    ['galleryImagesToDelete', galleryImagesToDelete],
    ['profileImagesToDelete', profileImagesToDelete],
    ['galleryOrder', galleryOrder],
    ['profileOrder', profileOrder]
  ]);

  return apiClient
    .post(UPLOAD_RESTAURANT_IMAGES, formData, {
      headers: { 'Content-Type': 'multipart/form-data', ...(restaurantID && { restaurantID }) }
    })
    .then((response) => response.data);
};
